import React from 'react'
import { convertToRaw } from 'draft-js'
import { Editor, EditorState } from 'react-draft-wysiwyg'
import Joi from 'joi'
import { t } from 'i18next'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import draftToHtml from 'draftjs-to-html'
import { Dialog } from '@mui/material'
import { Close as CloseIcon, NotificationsActiveOutlined, MailOutline, SendRounded } from '@mui/icons-material'
import { useDispatch } from '../../store'
import { ErrorsState, notRequired, preventCyrillicOnInput, validateForm } from '../../function/validation'
import { sendEmail, sendNotification } from '../../store/support/actionCreator'
import { Subscriber } from '../../store/user/reducer'
import { clearFieldError } from '../../store/common/actionCreator'
import { useAdminTheme } from '../../hook/useAdminTheme'
import l from '../../page/SubscribersPageV2.module.css'
import s from './NotifyDialogV2.module.css'

/*
 * Send a notification or an e-mail to subscribers, in the v2 admin design —
 * the classic PopupNotify's flow unchanged: the same rules (a title of 6–40
 * and a text of 6–300 for a notification, no Cyrillic; an e-mail of 6+), the
 * same rich-text editor for the e-mail, the same sendNotification / sendEmail.
 * The classic pages keep PopupNotify.
 */

const TITLE_MAX = 40
const NOTIFY_MAX = 300

const schemaBase = {
  mode: Joi.allow('notification', 'email'),
  emails: Joi.array().min(1).label(t('field.label.to')),
  title: Joi.string()
    .label(t('field.label.title'))
    .when('mode', { is: 'notification', then: Joi.string().min(6).max(TITLE_MAX).required(), otherwise: notRequired }),
}
const schemaNotification = Joi.object({ ...schemaBase, text: Joi.string().required().min(6).max(NOTIFY_MAX).label(t('field.label.text')) })
const schemaEmail = Joi.object({ ...schemaBase, text: Joi.string().required().min(6).label(t('field.label.text')) })

const editorToolbar = {
  options: ['inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign', 'colorPicker', 'link', 'history'],
}

export function NotifyDialogV2({ mode, subscribers: initSubscribers, onClose }: {
  mode: 'notification' | 'email'
  subscribers: Subscriber[]
  onClose: () => void
}) {
  const dispatch = useDispatch()
  const { theme } = useAdminTheme()
  const [subscribers, setSubscribers] = React.useState<Subscriber[]>(initSubscribers)
  const [title, setTitle] = React.useState('')
  const [text, setText] = React.useState('')
  const [email, setEmail] = React.useState<EditorState>()
  const [errors, setErrors] = React.useState<ErrorsState>(null)

  React.useEffect(() => { setErrors(null); dispatch(clearFieldError) }, [dispatch, title, text, email, subscribers])

  const emailText = React.useMemo(() => (email ? email.getCurrentContent().getPlainText() : ''), [email])
  const emailHtml = React.useMemo(() => (email ? draftToHtml(convertToRaw(email.getCurrentContent())) : ''), [email])

  const err = (k: string) => {
    const v = errors?.[k]
    return typeof v === 'string' ? v : undefined
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    const data = {
      mode,
      emails: subscribers.map((x) => x.email),
      title: title.trim(),
      text: mode === 'email' ? emailText.trim() : text.trim(),
    }
    const error = validateForm(mode === 'email' ? schemaEmail : schemaNotification, data)
    if (error) { setErrors(error); return }
    const ids = subscribers.map((x) => x.id)
    if (mode === 'notification') dispatch(sendNotification(data.title, data.text, ids))
    else {
      const form = new FormData()
      form.append('text', emailHtml)
      form.append('userIds', String(ids))
      dispatch(sendEmail(form))
    }
    onClose()
  }

  const isEmail = mode === 'email'
  return (
    <Dialog open onClose={onClose} maxWidth={false} PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', m: 2, maxHeight: 'calc(100vh - 32px)' } }}>
      <form className={`${l.theme} ${s.box}`} data-theme={theme} onSubmit={submit} noValidate>
        <div className={s.head}>
          <span className={s.icon}>{isEmail ? <MailOutline sx={{ fontSize: 19 }} /> : <NotificationsActiveOutlined sx={{ fontSize: 19 }} />}</span>
          <div className={s.headText}>
            <b>{isEmail ? 'Send e-mail' : 'Send notification'}</b>
            <span>{isEmail ? 'A message to the subscribers’ e-mail' : 'Appears in the subscribers’ notifications in the dashboard'}</span>
          </div>
          <button type="button" className={s.close} onClick={onClose} aria-label="Close"><CloseIcon sx={{ fontSize: 18 }} /></button>
        </div>

        <div className={s.body}>
          <div className={s.field}>
            <span className={s.label}>To <em>{subscribers.length} {subscribers.length === 1 ? 'subscriber' : 'subscribers'}</em></span>
            <div className={`${s.chips} ${err('emails') ? s.invalid : ''}`}>
              {subscribers.map((x) => (
                <span key={x.id} className={s.chip}>
                  {x.email}
                  <button type="button" onClick={() => setSubscribers((list) => list.filter((y) => y.id !== x.id))} aria-label={`Remove ${x.email}`}>×</button>
                </span>
              ))}
              {!subscribers.length && <span className={s.none}>Nobody — reopen this from the list to choose subscribers</span>}
            </div>
            {err('emails') && <span className={s.err}>{err('emails')}</span>}
          </div>

          {!isEmail && (
            <label className={s.field}>
              <span className={s.labelRow}>
                <span className={s.label}>Title</span>
                <span className={`${s.count} ${title.length > TITLE_MAX ? s.over : ''}`}>{title.length} / {TITLE_MAX}</span>
              </span>
              <input
                className={`${s.input} ${err('title') ? s.invalid : ''}`}
                value={title}
                onChange={(e) => preventCyrillicOnInput(e, setTitle, true)}
                onBlur={() => setTitle(title.trim())}
                placeholder="Your report is ready"
                autoFocus // eslint-disable-line jsx-a11y/no-autofocus
              />
              {err('title') && <span className={s.err}>{err('title')}</span>}
            </label>
          )}

          {isEmail ? (
            <div className={s.field}>
              <span className={s.label}>Text</span>
              <div className={`${s.editor} ${err('text') ? s.invalid : ''}`}>
                <Editor editorState={email} onEditorStateChange={setEmail} toolbar={editorToolbar} editorClassName={s.editorArea} placeholder="Write the e-mail…" />
              </div>
              {err('text') && <span className={s.err}>{err('text')}</span>}
            </div>
          ) : (
            <label className={s.field}>
              <span className={s.labelRow}>
                <span className={s.label}>Text</span>
                <span className={`${s.count} ${text.length > NOTIFY_MAX ? s.over : ''}`}>{text.length} / {NOTIFY_MAX}</span>
              </span>
              <textarea
                className={`${s.input} ${s.textarea} ${err('text') ? s.invalid : ''}`}
                rows={5}
                value={text}
                onChange={(e) => preventCyrillicOnInput(e, setText, true)}
                onBlur={() => setText(text.trim())}
                placeholder="What the subscribers should know"
              />
              {err('text') && <span className={s.err}>{err('text')}</span>}
            </label>
          )}
        </div>

        <div className={s.foot}>
          <span className={s.note}>{isEmail ? 'At least 6 characters' : 'Latin letters only — at least 6 characters each'}</span>
          <button type="button" className={s.ghost} onClick={onClose}>Cancel</button>
          <button type="submit" className={s.primary} disabled={!subscribers.length}><SendRounded sx={{ fontSize: 16 }} />Send</button>
        </div>
      </form>
    </Dialog>
  )
}
